import { useCallback, useEffect, useState } from 'react';
import { formatVideoTime } from './formatVideoTime';

/**
 * Playback state for {@link VideoPlayer} — supports no-skip-ahead when `preventSkipAhead` is true.
 * Pass `resetKey` to reset when the active video changes (e.g. guidance step id).
 */
export function useVideoPlayer({ resetKey, preventSkipAhead = false } = {}) {
  const [currentTime, setCurrentTime] = useState(0);
  const [maxWatched, setMaxWatched] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [duration, setDuration] = useState(0);

  useEffect(() => {
    setCurrentTime(0);
    setMaxWatched(0);
    setPlaying(false);
    setDuration(0);
  }, [resetKey]);

  const complete = duration > 0 && maxWatched >= duration - 0.5;

  const syncDuration = useCallback((nextDuration) => {
    if (Number.isFinite(nextDuration) && nextDuration > 0) {
      setDuration(nextDuration);
    }
  }, []);

  const syncProgress = useCallback((time) => {
    const safeTime = Math.max(0, time);
    setCurrentTime(safeTime);
    setMaxWatched((max) => Math.max(max, safeTime));
  }, []);

  const togglePlayback = useCallback(() => {
    if (complete) {
      return;
    }
    setPlaying((value) => !value);
  }, [complete]);

  const seek = useCallback(
    (nextTime) => {
      if (duration <= 0) {
        return null;
      }
      const clamped = Math.max(0, Math.min(duration, nextTime));
      if (!preventSkipAhead || clamped <= maxWatched + 0.25) {
        setCurrentTime(clamped);
        return clamped;
      }
      return null;
    },
    [duration, maxWatched, preventSkipAhead],
  );

  const handleEnded = useCallback(() => {
    setPlaying(false);
    if (duration > 0) {
      setCurrentTime(duration);
      setMaxWatched(duration);
    }
  }, [duration]);

  return {
    currentTime,
    maxWatched,
    playing,
    setPlaying,
    duration,
    complete,
    syncDuration,
    syncProgress,
    togglePlayback,
    seek,
    handleEnded,
    formatTime: formatVideoTime,
  };
}

export default useVideoPlayer;
